import React, { useEffect, useRef, useState } from "react";
import { useUpload, UploadItem } from "@/contexts/GlobalUploadContext";
import { X, ChevronDown, ChevronUp, CheckCircle, AlertCircle, Loader2, Video, RefreshCw, Upload } from "lucide-react";
import { cn } from "@/lib/utils";

const PANEL_WIDTH = 320; // w-80
const HEADER_HEIGHT = 56;
const EDGE = 8;

const clampPos = (x: number, y: number) => ({
  x: Math.min(Math.max(EDGE, x), Math.max(EDGE, window.innerWidth - PANEL_WIDTH - EDGE)),
  y: Math.min(Math.max(EDGE, y), Math.max(EDGE, window.innerHeight - HEADER_HEIGHT - EDGE)),
});

const GlobalUploadManager: React.FC = () => {
  const { uploadQueue, removeUpload, retryUpload } = useUpload();
  const [isMinimized, setIsMinimized] = useState(false);

  // Floating position. null = the default bottom-right anchor; set once the
  // user drags the header, so the panel can be moved off whatever it covers
  // (it sits over page controls, as an upload can outlive any one screen).
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{
    startX: number; startY: number; originX: number; originY: number; moved: boolean;
  } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Keep a dragged panel on-screen when the window shrinks.
  useEffect(() => {
    if (!pos) return;
    const onResize = () => setPos((p) => (p ? clampPos(p.x, p.y) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos !== null]);

  const handleHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Buttons inside the header keep their own behaviour.
    if ((e.target as HTMLElement).closest("button")) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: rect.left,
      originY: rect.top,
      moved: false,
    };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const handleHeaderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return; // click, so far
    drag.moved = true;
    setPos(clampPos(drag.originX + dx, drag.originY + dy));
  };

  const handleHeaderPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    // A press that never turned into a drag is the old minimize toggle.
    if (drag && !drag.moved) setIsMinimized((m) => !m);
  };

  if (uploadQueue.length === 0) return null;

  const uploadingCount = uploadQueue.filter(u => u.status === "uploading").length;
  const processingCount = uploadQueue.filter(u => u.status === "processing").length;
  const completedCount = uploadQueue.filter(u => u.status === "completed").length;
  const errorCount = uploadQueue.filter(u => u.status === "error").length;

  return (
    <div
      ref={panelRef}
      className={cn(
        "fixed w-80 bg-white dark:bg-gray-800 shadow-2xl rounded-xl border border-gray-200 dark:border-gray-700 z-[100000] overflow-hidden",
        // The size transition is kept, but not while positioned by drag -
        // transitioning left/top would make the panel lag behind the pointer.
        pos ? "" : "bottom-5 right-5 transition-all duration-300",
        isMinimized ? "h-14" : "max-h-[400px]"
      )}
      style={pos ? { left: pos.x, top: pos.y } : undefined}
    >
      {/* Header - drag anywhere on it to float the panel; a plain click still
          minimizes/expands as before. */}
      <div
        className="flex items-center justify-between p-4 bg-purple-600 text-white cursor-grab active:cursor-grabbing select-none touch-none"
        onPointerDown={handleHeaderPointerDown}
        onPointerMove={handleHeaderPointerMove}
        onPointerUp={handleHeaderPointerUp}
        onPointerCancel={() => { dragRef.current = null; }}
      >
        <div className="flex items-center gap-2">
          {uploadingCount + processingCount > 0 ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          <span className="font-semibold text-sm">
            {uploadingCount > 0
              ? `Uploading ${uploadingCount} file(s)...`
              : processingCount > 0
                // Distinct from "Uploading": the bytes are all sent and the
                // server is storing them. Saying "Uploading 100%" here was
                // indistinguishable from a finished upload that had silently
                // hung.
                ? `Processing ${processingCount} file(s) on server...`
                : errorCount > 0
                  ? `${errorCount} error(s)`
                  : "All uploads complete"}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {isMinimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </div>

      {/* Body */}
      {!isMinimized && (
        <div className="overflow-y-auto max-h-[340px] p-2 space-y-2">
          {uploadQueue.map((item) => (
            <UploadItemCard key={item.id} item={item} onRemove={removeUpload} onRetry={retryUpload} />
          ))}
        </div>
      )}
    </div>
  );
};

const UploadItemCard: React.FC<{ 
  item: UploadItem; 
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}> = ({ item, onRemove, onRetry }) => {
  // How long the server has been holding this one. A large class recording
  // genuinely takes a while to store, so elapsed time is the only honest signal
  // available -- there is no server-side progress to report. Ticking it makes
  // the difference between "working" and "stuck" visible without guessing on
  // the user's behalf.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (item.status !== "processing" || !item.processingSince) return;
    const tick = () =>
      setElapsed(Math.floor((Date.now() - item.processingSince!) / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [item.status, item.processingSince]);

  const elapsedLabel =
    elapsed >= 60 ? `${Math.floor(elapsed / 60)}m ${elapsed % 60}s` : `${elapsed}s`;

  return (
    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate dark:text-gray-200" title={item.title}>
            {item.title}
          </p>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
            {item.file.name}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {item.status === "error" && (
            <button 
              onClick={() => onRetry(item.id)}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-blue-500"
              title="Retry"
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          )}
          <button 
            onClick={() => onRemove(item.id)}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-400"
            title="Remove"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between text-[10px]">
          <span className={cn(
            "font-medium",
            item.status === "completed" ? "text-green-500" :
            item.status === "error" ? "text-red-500" :
            item.status === "processing" ? "text-purple-500" :
            "text-blue-500"
          )}>
            {item.status === "completed" ? "Completed" :
             item.status === "error" ? "Failed" :
             item.status === "processing" ? `Saving on server… ${elapsedLabel}` :
             item.status === "uploading" ? `${item.progress}%` : "Pending"}
          </span>
          {item.status === "completed" ? (
            <CheckCircle className="h-3 w-3 text-green-500" />
          ) : item.status === "error" ? (
            <AlertCircle className="h-3 w-3 text-red-500" />
          ) : null}
        </div>
        
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
          <div 
            className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              item.status === "completed" ? "bg-green-500" :
              item.status === "error" ? "bg-red-500" :
              item.status === "processing" ? "bg-purple-500 animate-pulse" :
              "bg-blue-500"
            )}
            style={{ width: `${item.progress}%` }}
          />
        </div>

        {/* Past a couple of minutes, say what is actually happening. A large
            recording is stored and queued for conversion server-side, and
            without this the only honest read of a long wait was "it's broken". */}
        {item.status === "processing" && elapsed > 120 && (
          <p className="text-[9px] text-purple-500 mt-1 italic">
            Large file — the server is still storing it. Keep this tab open until
            it finishes.
          </p>
        )}

        {item.error && (
          <p className="text-[9px] text-red-500 mt-1 italic line-clamp-1">
            {item.error}
          </p>
        )}
      </div>
    </div>
  );
};

export default GlobalUploadManager;
